// ---------------------------------------------------------------------------
// The Accounts tab: what the accounts department does in Odoo (invoices, supplier orders, customers), and
// recording in the CRM that it was done.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export interface AccountsInvoiceRequest {
  id: string; ref: string | null; projectId: string; projectNumber: string; projectName: string; customer: string | null;
  kind: string; description: string; percent: number | null; amount: number | null; status: string;
  odooInvoiceNo: string | null; invoicedDate: string | null; accountsNote: string | null; requestedBy: string; createdAt: string;
  /** PI (made outside Odoo, then paid) or tax invoice (made in Odoo). */
  docType: InvoiceDocType;
  piNo: string | null; piDate: string | null; piPaidDate: string | null;
  /** A tax invoice raised when a PI was paid: that PI's number, and the date it is due (15 days after the payment). */
  forPiNo: string | null; dueBy: string | null;
}
export type InvoiceDocType = 'pi' | 'tax';
/** ZATCA: the tax invoice within 15 days of receiving the payment on a PI. */
export const PI_TAX_INVOICE_DAYS = 15;
/** "PI-2026-014" stays as it is; "2026-014" reads "PI 2026-014". */
export const piLabel = (no: string | null | undefined) => (!no ? 'PI' : /^pi(?![a-z])/i.test(no.trim()) ? no.trim() : `PI ${no.trim()}`);
export interface AccountsPurchase {
  id: string; ref: string | null; projectId: string; projectNumber: string; projectName: string; description: string; system: string | null;
  supplier: string | null; amount: number | null; poNumber: string | null; status: string; decidedAt: string | null; neededBy: string | null;
  accountsDoneAt: string | null; odooRef: string | null;
}
export interface AccountsWonDeal {
  id: string; name: string; customer: string | null; customerPhone: string | null; address: string | null; amount: number | null;
  ownerName: string | null; wonAt: string | null; projectId: string | null; projectNumber: string | null; poNumber: string | null;
  doneAt: string | null; odooRef: string | null;
  /** Found in the nightly Odoo copy (still to mark done): a sales order or a customer invoice, and how it was found. */
  odooMatch: OdooWonMatch | null;
}
export interface OdooWonMatch { ref: string; kind: 'sale_order' | 'invoice'; by: 'po' | 'name_amount'; partner: string | null; amount: number | null }

/** "MNE-PO-001583 " and "mne po 001583" are the same reference: letters and digits only, lower case. */
const plainRef = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Finds a won deal in the Odoo copy, most certain first: a sales order whose customer reference is the deal's customer
 * PO, then a customer invoice whose reference or origin contains it, then a sales order for the same customer whose
 * amount (before VAT) is within 2% of the deal's. Null when nothing fits. Read only: Accounts decides with one click.
 */
export function matchWonDealInOdoo(
  deal: { poNumber: string | null; customer: string | null; amount: number | null },
  orders: { name: string; partner: string | null; amountUntaxed: number | null; customerRef: string | null }[],
  invoices: { name: string; partner: string | null; amountUntaxed: number | null; ref: string | null; origin: string | null }[],
): OdooWonMatch | null {
  const po = plainRef(deal.poNumber);
  if (po.length >= 4) {
    const so = orders.find((o) => plainRef(o.customerRef) === po);
    if (so) return { ref: so.name, kind: 'sale_order', by: 'po', partner: so.partner, amount: so.amountUntaxed };
  }
  if (po.length >= 5) {
    const inv = invoices.find((i) => plainRef(i.ref).includes(po) || plainRef(i.origin).includes(po));
    if (inv) return { ref: inv.name, kind: 'invoice', by: 'po', partner: inv.partner, amount: inv.amountUntaxed };
  }
  const customer = (deal.customer ?? '').trim().toLowerCase();
  if (customer.length >= 3 && deal.amount && deal.amount > 0) {
    const sameCustomer = (p: string | null) => { const x = (p ?? '').trim().toLowerCase(); return x.length >= 3 && (x.includes(customer) || customer.includes(x)); };
    const so = orders.find((o) => sameCustomer(o.partner) && o.amountUntaxed != null && Math.abs(o.amountUntaxed - deal.amount!) <= deal.amount! * 0.02);
    if (so) return { ref: so.name, kind: 'sale_order', by: 'name_amount', partner: so.partner, amount: so.amountUntaxed };
  }
  return null;
}
export interface AccountsProjectMoney {
  id: string; number: string; name: string; customer: string | null; status: string;
  contractValue: number; variations: number; invoiced: number; pendingRequests: number; toInvoice: number; retentionPct: number | null;
}
export interface AccountsOverview {
  /** Accounts staff and administrators record what was done in Odoo; project managers only see. */
  canAct: boolean;
  invoiceRequests: AccountsInvoiceRequest[];
  purchases: AccountsPurchase[];
  wonDeals: AccountsWonDeal[];
  projects: AccountsProjectMoney[];
}

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const ref = z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(100).nullable()).optional();
export const invoicedSchema = z.object({ odooInvoiceNo: z.string().trim().min(1, 'Enter the Odoo invoice number').max(100), invoicedDate: date.optional() }).strict();
export const returnRequestSchema = z.object({ note: z.string().trim().min(1, 'Say why it is returned').max(1000) }).strict();
/** PI sent to the customer (made outside Odoo): its number and date. */
export const piSentSchema = z.object({ piNo: z.string().trim().min(1, 'Enter the PI number').max(100), piDate: date.optional() }).strict();
/** The customer paid the PI: the tax invoice request follows. */
export const piPaidSchema = z.object({ paidDate: date.optional() }).strict();
export const doneInOdooSchema = z.object({ odooRef: ref }).strict();

/** Accounts → Ready to invoice: billable work with no invoice yet (worked out by the CRM; nothing is invoiced automatically). */
export interface InvoiceSuggestion {
  /** Stable name of the suggestion (what "requested" / "not yet" is recorded against). */
  key: string;
  kind: 'milestone' | 'delivered' | 'advance' | 'progress';
  projectId: string | null; projectNumber: string | null; projectName: string; dealId: string | null; customer: string | null;
  title: string; reason: string; percent: number | null; amount: number | null;
  /** Since when it could be invoiced (YYYY-MM-DD). */
  since: string;
  /** What to request: a PI for an advance payment, a tax invoice for the rest (Accounts can change it). */
  docType: InvoiceDocType;
}
export const invoiceSuggestionActSchema = z.object({
  key: z.string().min(3).max(120),
  action: z.enum(['request', 'odoo', 'snooze']),
  /** Already invoiced directly in Odoo: its invoice number. */
  invoiceNo: z.string().trim().min(1).max(100).optional(),
  amount: z.number().positive().max(1e12).optional(),
  /** Request: a PI or a tax invoice (default: the suggestion's). */
  docType: z.enum(['pi', 'tax']).optional(),
  reason: z.string().trim().max(500).optional(),
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
